// src/pages/services/PowerBIReporting.js
import React from 'react';
import { Link } from 'react-router-dom';
import styles from './PowerBIReporting.module.css';

const PowerBIReporting = () => {
  return (
    <div className={styles.container}>
      {/* Hero Section */}
      <section className={styles.hero}>
        <h1 className={styles.heroTitle}>Power BI Dashboard & Reporting Solutions</h1>
        <p className={styles.heroText}>
          Turn complex project data into <strong>real-time, decision-ready insights</strong> — with dashboards that tell your project’s story at a glance.
        </p>
        <Link to="/contact" className={styles.heroButton}>
          See a Live Demo
        </Link>
      </section>

      {/* Intro Section */}
      <section className={styles.section}>
        <div className={styles.contentContainer}>
          <p className={styles.introText}>
            At Nulek Consulting, we deliver advanced Power BI reporting solutions that transform complex project data into clear, interactive, and highly visual dashboards. Our reporting services empower project teams and executives with real-time insights for effective decision-making across planning, scheduling, risk, cost, and overall project performance.
          </p>

          <h2 className={styles.sectionTitle}>Our Power BI Reporting Services</h2>

          <ul className={styles.serviceList}>
            <li className={styles.serviceItem}>
              <h3 className={styles.serviceTitle}>Custom Dashboard Development</h3>
              <p className={styles.serviceDesc}>
                Bespoke dashboards designed for your project's unique KPIs — from site-level productivity to portfolio performance — with intuitive navigation and filter controls.
              </p>
            </li>

            <li className={styles.serviceItem}>
              <h3 className={styles.serviceTitle}>Integration With Project Scheduling Tools</h3>
              <p className={styles.serviceDesc}>
                Seamless live connection to Primavera P6, MS Project, and Excel — ensuring dashboards reflect the latest schedule updates without manual intervention.
              </p>
            </li>

            <li className={styles.serviceItem}>
              <h3 className={styles.serviceTitle}>Data Modelling & Analytics</h3>
              <p className={styles.serviceDesc}>
                Clean, structured data models that unify schedule, cost, risk, and resource data — enabling advanced analytics, trend detection, and predictive insights.
              </p>
            </li>

            <li className={styles.serviceItem}>
              <h3 className={styles.serviceTitle}>Reporting for Earned Value Management (EVM)</h3>
              <p className={styles.serviceDesc}>
                Real-time CPI, SPI, VAC, EAC, and forecast-to-complete metrics — visualised with variance trends, burn-down curves, and risk-adjusted projections.
              </p>
            </li>

            <li className={styles.serviceItem}>
              <h3 className={styles.serviceTitle}>Executive & Portfolio-Level Reporting</h3>
              <p className={styles.serviceDesc}>
                High-level dashboards for boards and governance bodies — showing portfolio health, milestone confidence, resource demand, and strategic risk exposure.
              </p>
            </li>
          </ul>
        </div>
      </section>

      {/* Dashboard Showcase Section */}
      <section className={styles.dashboardSection}>
        <div className={styles.contentContainer}>
          <h2 className={styles.dashboardTitle}>Sample Dashboard Capabilities</h2>
          <p className={styles.dashboardSubtitle}>
            Interactive, role-based views that grow with your project — from tender to closeout.
          </p>

          <div className={styles.dashboardGrid}>
            <div className={styles.dashboardCard}>
              <div className={styles.visualPlaceholder}>
                📊 Construction Progress Dashboard
              </div>
              <h3 className={styles.dashboardCardTitle}>Look-Ahead & Critical Path Tracker</h3>
              <p className={styles.dashboardCardDesc}>
                Real-time Gantt with lookahead window, critical path highlighting, delay alerts, and recovery plan overlay.
              </p>
            </div>

            <div className={styles.dashboardCard}>
              <div className={styles.visualPlaceholder}>
                📈 EVM Performance Dashboard
              </div>
              <h3 className={styles.dashboardCardTitle}>Earned Value Analytics Hub</h3>
              <p className={styles.dashboardCardDesc}>
                SPI/CPI trends, variance root-cause analysis, forecast confidence bands, and resource-loaded S-curves.
              </p>
            </div>

            <div className={styles.dashboardCard}>
              <div className={styles.visualPlaceholder}>
                🗺️ Linear Project View
              </div>
              <h3 className={styles.dashboardCardTitle}>Time–Chainage Integration</h3>
              <p className={styles.dashboardCardDesc}>
                TILOS/Turbo-Chart visuals embedded in Power BI — showing progress along rail, road, or pipeline corridors.
              </p>
            </div>

            <div className={styles.dashboardCard}>
              <div className={styles.visualPlaceholder}>
                📊 Portfolio Heatmap
              </div>
              <h3 className={styles.dashboardCardTitle}>Portfolio Risk & Performance</h3>
              <p className={styles.dashboardCardDesc}>
                Traffic-light status, milestone slippage heatmaps, resource demand vs capacity, and funding burn rates.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PowerBIReporting;